/**
 * The single shared resolver for `{{group.key}}` attribute tokens, composed additively into BOTH
 * substitution seams (report/prose in `modules/report/tokens.ts` and AI prompts in `config/prompts.ts`).
 *
 * Its regex (`/\{\{([a-z0-9_.]+)\}\}/g`) is disjoint from the legacy engines' regexes, so running this
 * as an extra pass never disturbs a legacy `[Bracket]` or `{token}` (see `catalog.ts` header). An
 * unknown key, or a known key whose data is absent, is left VERBATIM — never blanked. This is the
 * "no blank leak" contract: a token for data not yet available at this point in the campaign survives
 * as `{{…}}` rather than collapsing to an empty string in customer-facing prose.
 */

import type { AttributeContext } from './context';
import { lookupAttribute } from './catalog';

const TOKEN_RE = /\{\{([a-z0-9_.]+)\}\}/g;

/**
 * Replace every `{{group.key}}` in `text` with its resolved value, leaving unknown or not-yet-available
 * tokens verbatim. Single pass over the source text (a resolved value that itself contains `{{…}}` is
 * inserted literally and not re-scanned), matching the single-pass behaviour of both legacy engines.
 */
export function resolveTokens(text: string, ctx: AttributeContext): string {
  if (!text) return text;
  return text.replace(TOKEN_RE, (whole, key: string) => {
    const found = lookupAttribute(key);
    if (!found) return whole;
    const { def, match } = found;
    // Availability gate (picker-assigned instance order): before the originating step ⇒ verbatim.
    const after = def.availability?.afterStepOrder;
    if (after != null && (ctx.currentStepOrder ?? Infinity) <= after) return whole;
    const value = def.resolve(ctx, match);
    return value === undefined ? whole : value;
  });
}

/** True when `text` contains at least one `{{…}}` attribute token. */
export function hasAttributeTokens(text: string): boolean {
  return /\{\{[a-z0-9_.]+\}\}/.test(text);
}
